import { useCallback, useEffect, useRef, useState } from 'react';
import { DatabaseBackup, History, RotateCcw, Users, RefreshCw, Download, Upload } from '../../lib/icons';
import { toast } from 'sonner';
import {
  fetchSauvegardes,
  creerSauvegarde,
  restaurerSauvegarde,
  telechargerSauvegarde,
  importerSauvegarde,
  fetchActivite,
  posteId,
} from '../../lib/store';
import { useSondage } from '../../lib/sondage';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const heure = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');

export const SauvegardesEtActivite = () => {
  const [sauvegardes, setSauvegardes] = useState([]);
  const [activite, setActivite] = useState([]);
  const [exportEnCours, setExportEnCours] = useState(false);
  const fichierRef = useRef(null);

  const exporterUsb = async () => {
    setExportEnCours(true);
    try {
      const nom = await telechargerSauvegarde();
      toast.success(`Fichier ${nom} téléchargé — copiez-le sur votre clé USB`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExportEnCours(false);
    }
  };

  const importerUsb = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!window.confirm(`Restaurer la base depuis « ${f.name} » ? L'état actuel sera sauvegardé avant écrasement.`)) return;
    try {
      const r = await importerSauvegarde(f);
      toast.success(`Base restaurée depuis le fichier (${r.cles} collections) — rechargement…`);
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const charger = useCallback(async () => {
    setSauvegardes(await fetchSauvegardes());
    setActivite(await fetchActivite());
  }, []);

  const rafraichirActivite = useCallback(async () => {
    const lignes = await fetchActivite();
    setActivite(lignes);
    return [lignes.length, lignes[0]?.date ?? null];
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);
  useSondage(rafraichirActivite, 10000);

  const restaurer = async (s) => {
    if (!window.confirm(`Restaurer la sauvegarde du ${heure(s.date)} ? L'état actuel sera sauvegardé avant écrasement.`)) return;
    try {
      await restaurerSauvegarde(s.id);
      toast.success('Base restaurée — rechargement…');
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <section data-testid="section-sauvegardes" className="border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <DatabaseBackup size={16} /> Sauvegardes de la base
            </h2>
            <p className="text-xs text-slate-500">
              Copie complète automatique chaque soir à 23h30 (30 versions conservées) — restauration en un clic. « Télécharger sur clé USB » produit un fichier .json complet à conserver hors ligne.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="sauvegarde-export-usb"
            disabled={exportEnCours}
            onClick={exporterUsb}
            className="flex h-10 items-center gap-2 border border-emerald-700 bg-emerald-700 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:opacity-60"
          >
            <Download size={15} /> {exportEnCours ? 'Préparation…' : 'Télécharger sur clé USB'}
          </button>
          <button
            type="button"
            data-testid="sauvegarde-import-usb"
            onClick={() => fichierRef.current?.click()}
            className="flex h-10 items-center gap-2 border border-slate-300 px-3 font-heading text-sm font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <Upload size={15} /> Restaurer depuis un fichier
          </button>
          <input ref={fichierRef} type="file" accept=".json,application/json" className="hidden" data-testid="sauvegarde-import-fichier" onChange={importerUsb} />
          <button
            type="button"
            data-testid="sauvegarde-maintenant"
            onClick={async () => {
              try {
                const r = await creerSauvegarde();
                toast.success(`Sauvegarde ${r.id} créée (${r.cles} collections, ${r.lignes} enregistrements)`);
                charger();
              } catch (e) {
                toast.error(e.message);
              }
            }}
            className="flex h-10 items-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <History size={15} /> Sauvegarder maintenant
          </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-sauvegardes">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                {['Sauvegarde', 'Date & heure', 'Origine', 'Collections', 'Enregistrements', ''].map((c, i) => (
                  <th key={c || i} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sauvegardes.length === 0 && (
                <tr>
                  <td colSpan={6} data-testid="sauvegardes-vide" className="px-3 py-8 text-center text-sm text-slate-500">
                    Aucune sauvegarde pour l'instant — la première sera créée automatiquement ce soir.
                  </td>
                </tr>
              )}
              {sauvegardes.map((s) => (
                <tr key={s.id} data-testid={`sauvegarde-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{s.id}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{heure(s.date)}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">{s.origine}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{s.cles}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{s.lignes}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`restaurer-${s.id}`}
                      onClick={() => restaurer(s)}
                      className="flex h-9 items-center gap-1.5 border border-slate-300 px-2 text-xs font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                    >
                      <RotateCcw size={13} /> Restaurer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="section-activite" className="border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <Users size={16} /> Journal multi-postes (temps réel)
            </h2>
            <p className="text-xs text-slate-500">
              Qui a saisi quoi, depuis quel poste — actualisé toutes les 10 s. Ce poste : <b>{posteId}</b>
            </p>
          </div>
          <button
            type="button"
            data-testid="activite-refresh"
            onClick={charger}
            className="flex h-10 items-center gap-1.5 border border-slate-300 px-3 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <RefreshCw size={13} /> Actualiser
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full border-collapse text-left" data-testid="table-activite">
            <thead className="sticky top-0 border-b border-slate-200 bg-slate-50">
              <tr>
                {['Heure', 'Utilisateur', 'Rôle', 'Poste', 'Module modifié'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activite.length === 0 && (
                <tr>
                  <td colSpan={5} data-testid="activite-vide" className="px-3 py-8 text-center text-sm text-slate-500">
                    Aucune activité enregistrée pour le moment.
                  </td>
                </tr>
              )}
              {activite.map((a) => (
                <tr key={a.id} className={`hover:bg-slate-50 ${a.poste === posteId ? 'bg-slate-50' : ''}`}>
                  <td className="px-3 py-1.5 font-data text-xs text-slate-500">{heure(a.horodatage)}</td>
                  <td className="px-3 py-1.5 text-sm font-semibold text-slate-900">{a.utilisateur}</td>
                  <td className="px-3 py-1.5 text-xs uppercase text-slate-600">{a.role}</td>
                  <td className="px-3 py-1.5 font-data text-xs text-slate-700">
                    {a.poste}
                    {a.poste === posteId && <span className="ml-1 text-[10px] text-slate-400">(ce poste)</span>}
                  </td>
                  <td className="px-3 py-1.5 font-data text-xs text-slate-700">
                    {(a.collection ?? '').replace('oliva.', '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};
